import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { History, ImagePlus, Loader2, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn, getInitials } from '@/lib/utils';
import { useEmployee, useEmployees, useUpdateEmployee } from '../../api/employees';
import { useRoleDefinitions } from '../../api/roleDefinitions';
import { EMPLOYMENT_TYPES, type EmploymentType, type UpdateEmployeeInput } from '../../types';
import { RoleAssignPanel } from './RoleAssignPanel';

interface Props {
  employeeId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '--';
  return new Date(iso).toLocaleString('en-CA', { timeZone: 'America/Montreal' });
}

export function EmployeeDetailPanel({ employeeId, open, onOpenChange }: Props) {
  const { data: employee, isLoading } = useEmployee(open ? employeeId : null);
  const { data: roleDefsData } = useRoleDefinitions();
  const { data: managersList } = useEmployees();
  const update = useUpdateEmployee();

  const [employmentType, setEmploymentType] = useState<EmploymentType | ''>('');
  const [managerId, setManagerId] = useState<string>('');
  const [position, setPosition] = useState('');
  const [phone, setPhone] = useState('');
  const [extension, setExtension] = useState('');
  const [mobile, setMobile] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync from server when the selected employee changes.
  // Guarded on employee?.id so refetches don't wipe in-progress edits.
  useEffect(() => {
    if (employee) {
      setEmploymentType((employee.employment_type as EmploymentType) || '');
      setManagerId(employee.manager_user_id || '');
      setPosition(employee.position || '');
      setPhone(employee.phone || '');
      setExtension(employee.extension || '');
      setMobile(employee.mobile || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.id]);

  const save = (patch: UpdateEmployeeInput) => {
    if (!employeeId) return;
    update.mutate({ id: employeeId, patch });
  };

  const uploadPhotoFile = async (file: File) => {
    if (!employeeId) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setPhotoUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await fetch('/api/v4/profile-photos/upload', {
        method: 'POST',
        body: form,
        credentials: 'include',
      });
      if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`);
      const data = (await resp.json()) as { url: string };
      save({ profile_photo_url: data.url });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      toast.error('Photo upload failed', { description: message });
    } finally {
      setPhotoUploading(false);
    }
  };

  const handlePhotoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) uploadPhotoFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadPhotoFile(file);
  };

  const roleDefs = roleDefsData?.roles ?? [];
  const assignedRoleSlugs = (employee?.roles || []).map((r) => r.role);
  const managerCandidates = (managersList?.employees ?? []).filter((e) => e.id !== employeeId);
  const fullName =
    [employee?.firstname, employee?.lastname].filter(Boolean).join(' ') ||
    employee?.email ||
    'Employee';

  if (!open) return null;

  return (
    <div className="h-full flex flex-col border-l bg-background overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold truncate">{fullName}</span>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onOpenChange(false)}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {isLoading || !employee ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Photo */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              className={cn(
                'flex items-center gap-4 rounded-lg border border-dashed p-3 transition-colors',
                dragActive ? 'border-blue-400 bg-blue-50' : 'border-muted',
              )}
            >
              <Avatar className="h-16 w-16">
                {employee.profile_photo_url && (
                  <AvatarImage src={employee.profile_photo_url} alt={fullName} />
                )}
                <AvatarFallback className="bg-blue-100 text-blue-700">
                  {getInitials(fullName)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={photoUploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {photoUploading ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <ImagePlus className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {photoUploading ? 'Uploading…' : 'Upload photo'}
                </Button>
                <p className="text-[11px] text-muted-foreground">
                  Drag & drop an image here, or click to pick a file.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoPick}
                />
              </div>
            </div>

            {/* Profile */}
            <div className="grid grid-cols-2 gap-3">
              <Section label="Email">
                <Input value={employee.email ?? ''} readOnly disabled className="text-sm" />
              </Section>
              <Section label="Position">
                <Input
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  onBlur={() => {
                    const next = position || null;
                    if (next !== (employee.position ?? null)) save({ position: next });
                  }}
                  placeholder="e.g. Senior Engineer"
                  className="text-sm"
                />
              </Section>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-[1fr_96px] gap-3">
              <Section label="Phone">
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onBlur={() => {
                    const next = phone || null;
                    if (next !== (employee.phone ?? null)) save({ phone: next });
                  }}
                  placeholder="+1 514 555 0100"
                  className="text-sm"
                />
              </Section>
              <Section label="Ext.">
                <Input
                  value={extension}
                  onChange={(e) => setExtension(e.target.value)}
                  onBlur={() => {
                    const next = extension || null;
                    if (next !== (employee.extension ?? null)) save({ extension: next });
                  }}
                  className="text-sm"
                />
              </Section>
            </div>
            <Section label="Mobile">
              <Input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                onBlur={() => {
                  const next = mobile || null;
                  if (next !== (employee.mobile ?? null)) save({ mobile: next });
                }}
                placeholder="+1 514 555 0100"
                className="text-sm"
              />
            </Section>

            {/* Employment */}
            <div className="grid grid-cols-2 gap-3">
              <Section label="Type">
                <Select
                  value={employmentType || 'none'}
                  onValueChange={(v) => {
                    const next = v === 'none' ? '' : (v as EmploymentType);
                    setEmploymentType(next);
                    save({ employment_type: next || null });
                  }}
                >
                  <SelectTrigger className="text-sm"><SelectValue placeholder="Not set" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {EMPLOYMENT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Section>
              <Section label="Manager">
                <Select
                  value={managerId || 'none'}
                  onValueChange={(v) => {
                    const next = v === 'none' ? '' : v;
                    setManagerId(next);
                    save({ manager_user_id: next || null });
                  }}
                >
                  <SelectTrigger className="text-sm"><SelectValue placeholder="No manager" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No manager</SelectItem>
                    {managerCandidates.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {[m.firstname, m.lastname].filter(Boolean).join(' ') || m.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Section>
            </div>

            {/* Roles */}
            <Section label="Roles & Access">
              <RoleAssignPanel
                employeeId={employee.id}
                assignedRoles={assignedRoleSlugs}
                roleDefinitions={roleDefs}
              />
            </Section>

            {/* Audit history */}
            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-2 text-xs font-semibold uppercase text-muted-foreground hover:text-foreground">
                <History className="h-3.5 w-3.5" />
                Access history ({employee.audit.length})
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                {employee.audit.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No role changes recorded yet.</p>
                ) : (
                  <ul className="space-y-1 text-xs">
                    {employee.audit.map((entry) => {
                      const actor = entry.actor
                        ? [entry.actor.firstname, entry.actor.lastname].filter(Boolean).join(' ') || entry.actor.email
                        : 'system';
                      return (
                        <li key={entry.id} className="border-l-2 border-muted pl-3">
                          <span className="text-muted-foreground">[{formatDateTime(entry.created_at)}]</span>{' '}
                          <span className="font-medium">{actor}</span>{' '}
                          <span className={entry.action === 'granted' ? 'text-emerald-700' : 'text-red-700'}>
                            {entry.action}
                          </span>{' '}
                          <span className="font-mono">{entry.role}</span>
                          {entry.reason && <span className="text-muted-foreground"> — "{entry.reason}"</span>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CollapsibleContent>
            </Collapsible>
          </>
        )}
      </div>
    </div>
  );
}
